import assessmentsApi from '@/app/redux/endpoints/assessments';
import { useBulkDeleteStudentsMutation } from '@/app/redux/endpoints/students';
import { useAppDispatch } from '@/app/redux/hooks';
import { useOrganisation } from '@/app/redux/hooks/organisation';
import { useStudents } from '@/app/redux/hooks/students';
import { useUser } from '@/app/redux/hooks/user';
import {
  refreshModals,
  setContactFormModalOpen,
} from '@/app/redux/slices/modalsSlice';
import { UNKNOWN_ERROR_MESSAGE } from '@/common/ui/UnknownErrorMessage';
import { handleLogout } from '@/common/utils/auth';
import trackMixpanelEvent from '@/common/utils/mixpanel';
import { LearnerMultiSelect } from '@/dashboard/MultiSelect';
import {
  ArrowUpRightIcon,
  InfoIcon,
  SignOutIcon,
  WarningCircleIcon,
} from '@phosphor-icons/react';
import { Alert, AlertDescription } from '@shadcn/components/alert';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@shadcn/components/alert-dialog';
import { Button } from '@shadcn/components/button';
import { Separator } from '@shadcn/components/separator';
import { chunk } from 'lodash';
import { useState } from 'react';
import { toast } from 'sonner';

interface OverSeatLimitModalProps {
  setOpen: (open: boolean) => void;
}
export default function OverSeatLimitModal({
  setOpen,
}: OverSeatLimitModalProps) {
  const dispatch = useAppDispatch();

  const { students } = useStudents();
  const { user } = useUser();
  const { organisation, currentOrganisationId } = useOrganisation();

  const [bulkDeleteStudents] = useBulkDeleteStudentsMutation();

  const userId = user?.uid;
  const numStudents = students.length;
  const seatCount = organisation?.license.seatCount ?? 0;

  const [error, setError] = useState<JSX.Element | string | null>(null);
  const [selectedStudentIds, setSelectedStudentIds] = useState<string[]>([]);

  async function handleDeleteSelectedStudents() {
    if (!userId || !currentOrganisationId) {
      console.error('User ID or org ID is missing');
      setError(UNKNOWN_ERROR_MESSAGE);
      return;
    }

    const idsToDelete = [...selectedStudentIds];
    const total = idsToDelete.length;

    // Close dialog immediately, show progress toast
    setOpen(false);

    const chunks = chunk(idsToDelete, 50);
    let deleted = 0;

    const toastId = toast.loading('Deleting learners...', {
      description: "Please don't refresh or leave the page",
    });

    try {
      for (const idChunk of chunks) {
        const { deletedAssessmentIds } = await bulkDeleteStudents({
          orgId: currentOrganisationId,
          studentIds: idChunk,
        }).unwrap();
        if (deletedAssessmentIds.length > 0) {
          dispatch(
            assessmentsApi.util.updateQueryData(
              'getAssessmentsForOrganisation',
              currentOrganisationId!,
              (draft) => {
                const ids = new Set(deletedAssessmentIds);
                return draft.filter((a) => !ids.has(a.id));
              },
            ),
          );
        }
        deleted += idChunk.length;
        toast.loading(`Deleting learners (${deleted}/${total})`, {
          id: toastId,
        });
      }

      trackMixpanelEvent('Learners Deleted (Over Seat Limit)', {
        numStudentsDeleted: total,
        studentIds: idsToDelete,
      });

      dispatch(refreshModals());
      toast.success('Students deleted', { id: toastId, description: '' });
    } catch (error) {
      console.error('Error deleting students:', error);
      if (deleted > 0) {
        toast.error('Failed to delete learners', {
          id: toastId,
          description: `Deleted ${deleted} of ${total}. Please retry.`,
        });
      } else {
        toast.error('Failed to delete learners', {
          id: toastId,
          description: UNKNOWN_ERROR_MESSAGE,
        });
      }
    }
  }

  return (
    <AlertDialog open={true}>
      <AlertDialogContent className="max-w-3xl">
        <AlertDialogHeader className="mb-4">
          <AlertDialogTitle className="flex items-center justify-between">
            Learner Limit Exceeded
            <Button
              variant="outline"
              size="sm"
              className="flex gap-2"
              onClick={handleLogout}
            >
              <SignOutIcon />
              Sign out
            </Button>
          </AlertDialogTitle>

          <AlertDialogDescription className="flex flex-col gap-1">
            <span>
              You have {numStudents} learners, but your subscription only allows
              for {seatCount}.
            </span>{' '}
            <span>
              To continue using Spellcaster, please select at least{' '}
              {numStudents - seatCount} learner
              {numStudents - seatCount === 1 ? '' : 's'} to delete, or upgrade
              your plan.
            </span>
          </AlertDialogDescription>
        </AlertDialogHeader>

        {error && (
          <Alert variant="destructive">
            <WarningCircleIcon className="h-4 w-4 ml-2" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        <div className="flex justify-center gap-4 w-full">
          <div className="flex flex-col justify-between gap-4 w-full">
            <span className="text-base font-medium">Delete Learners</span>
            <LearnerMultiSelect
              items={students}
              onSelect={(id) =>
                setSelectedStudentIds((prev) =>
                  prev.includes(id)
                    ? prev.filter((i) => i !== id)
                    : [...prev, id],
                )
              }
              selectedIds={selectedStudentIds}
              onFilter={(searchTerm) => {
                return students.filter(
                  (s) =>
                    s.studentFirstName
                      .toLowerCase()
                      .includes(searchTerm.toLowerCase()) ||
                    s.studentSurname
                      .toLowerCase()
                      .includes(searchTerm.toLowerCase()) ||
                    s.spellcastUsername
                      .toLowerCase()
                      .includes(searchTerm.toLowerCase()),
                );
              }}
            />
            <Button
              variant="destructive"
              onClick={handleDeleteSelectedStudents}
              disabled={selectedStudentIds.length < numStudents - seatCount}
            >
              Delete Learners
            </Button>
          </div>

          <div className="flex flex-col items-center justify-center gap-2 my-4 mx-2">
            <Separator orientation="vertical" className="h-1/2" />
            <em className="text-muted-foreground">or</em>
            <Separator orientation="vertical" className="h-1/2" />
          </div>

          <div className="flex flex-col justify-between gap-4 w-full">
            <span className="text-base font-medium">Upgrade Plan</span>
            <Alert className="flex items-center gap-2 h-full">
              <InfoIcon size={18} className="ml-1" />
              <div className="flex flex-col gap-2">
                <span>
                  To upgrade your plan, please get in touch with{' '}
                  <span
                    className="underline cursor-pointer"
                    onClick={() => {
                      setOpen(false);
                      dispatch(setContactFormModalOpen(true));
                    }}
                  >
                    rupert@spellcaster.education
                  </span>
                </span>
              </div>
            </Alert>
            <Button
              className="gap-2"
              onClick={() => {
                setOpen(false);
                dispatch(setContactFormModalOpen(true));
              }}
            >
              Open Contact Form
              <ArrowUpRightIcon size={20} />
            </Button>
          </div>
        </div>
      </AlertDialogContent>
    </AlertDialog>
  );
}
